// Les envies d'achats inutiles : plus on garde d'argent sous la main, plus elles arrivent.
// Pendant le job d'été, la chance se tente à chaque heure travaillée ; ensuite, chaque semaine de jeu.
import { IMPULSE_ITEMS, type ImpulseItem } from '../content/impulses';
import { nextRandom, pickWeighted } from '../engine/rng';
import type { GameState } from '../engine/state';
import { atTodayPrices, monthlySpending } from './economy';
import { startEvent } from './events';
import { currentMood } from './wellbeing';

export const SUMMER_TEMPTATION = {
  /** En dessous de ce montant en poche, pas d'envie. */
  threshold: 40,
  /** Chaque tranche de 40 € au-dessus du seuil ajoute 1 % de chance par heure travaillée. */
  divisor: 4000,
  maxChance: 0.07,
  /** Au moins dix heures de travail entre deux envies, et quatre envies au plus. */
  cooldownClicks: 10,
  maxCount: 4,
};

export const WEEKLY_TEMPTATION = {
  /** L'argent au-delà d'un mois de dépenses (et d'au moins 200 €) attire les envies. */
  minThreshold: 200,
  maxChance: 0.06,
  /** Au moins deux mois de jeu entre deux envies, et quinze jours après tout autre événement. */
  minDaysBetween: 60,
  /** L'envie prend la place de l'événement d'une semaine, jamais deux dans la même semaine. */
  minDaysAfterEvent: 6,
};

/** Seuil au-delà duquel l'argent du compte courant « dort » sous les yeux. */
export function idleCashThreshold(state: GameState): number {
  return Math.max(WEEKLY_TEMPTATION.minThreshold, monthlySpending(state));
}

export function summerTemptationChance(cash: number): number {
  const excess = Math.max(0, cash - SUMMER_TEMPTATION.threshold);
  return Math.min(SUMMER_TEMPTATION.maxChance, excess / SUMMER_TEMPTATION.divisor);
}

export function weeklyTemptationChance(state: GameState): number {
  const threshold = idleCashThreshold(state);
  const excess = Math.max(0, state.cash - threshold);
  return Math.min(WEEKLY_TEMPTATION.maxChance, excess / (20 * threshold)) * currentMood(state).temptation;
}

/** Une envie qu'on pourrait se payer tout de suite, sans la répéter plus de deux fois. */
export function pickImpulseItem(state: GameState): ImpulseItem | null {
  const adult = state.stage !== 'prologue';
  const candidates = IMPULSE_ITEMS.filter(
    (item) =>
      (adult || !item.adult) &&
      atTodayPrices(state, item.price) <= state.cash * 0.8 &&
      (state.eventCounts[`envie_${item.id}`] ?? 0) < 2,
  );
  if (candidates.length === 0) return null;
  // Avec un salaire, les envies d'adulte sont plus fréquentes ; les envies déjà vues reviennent moins.
  return pickWeighted(state, candidates, (item) => {
    const seen = (state.eventCounts[`envie_${item.id}`] ?? 0) > 0;
    return (adult && item.adult ? 3 : 1) * (seen ? 1 : 3);
  });
}

/** Appelé à chaque heure travaillée pendant le job d'été. */
export function maybeSummerTemptation(state: GameState): void {
  if (state.pendingEvent || state.prologueStep !== 'summer') return;
  if (state.summerTemptations >= SUMMER_TEMPTATION.maxCount) return;
  if (state.summerClicks - state.lastTemptationClick < SUMMER_TEMPTATION.cooldownClicks) return;
  // Quand le moral est au plus bas, on dépense plus facilement pour se consoler.
  if (nextRandom(state) >= summerTemptationChance(state.cash) * currentMood(state).temptation) return;
  const item = pickImpulseItem(state);
  if (!item) return;
  state.summerTemptations += 1;
  state.lastTemptationClick = state.summerClicks;
  startEvent(state, `envie_${item.id}`);
}

/** Appelé une fois par semaine de jeu ; renvoie vrai si une envie a été déclenchée. */
export function maybeCashTemptation(state: GameState): boolean {
  if (state.pendingEvent) return false;
  if (state.day - state.lastTemptationDay < WEEKLY_TEMPTATION.minDaysBetween) return false;
  if (state.day - state.lastEventDay < WEEKLY_TEMPTATION.minDaysAfterEvent) return false;
  if (nextRandom(state) >= weeklyTemptationChance(state)) return false;
  const item = pickImpulseItem(state);
  if (!item) return false;
  state.lastTemptationDay = state.day;
  startEvent(state, `envie_${item.id}`);
  return true;
}
